import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, Clock, DollarSign, Percent, Shield } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { useTimezone, TimezonePicker, PlayerWithdrawalAutoApprovePanel, PAGE } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { fetchDashboard, fetchSuperAgentPlayerWithdrawalAutoApprove, patchAgentLowBalanceWarning, patchPortalTwoFactor } from '../services/api';
import type { PlayerWithdrawalAutoApproveSnapshot } from '../services/api';
import { formatCostRate } from '../utils/costFormat';

const MIN_LOW_BALANCE_USD = 50;

export default function SettingsPage() {
  const { timezone } = useTimezone();
  const { user, token, login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lowBalEnabled, setLowBalEnabled] = useState(true);
  const [lowBalThreshold, setLowBalThreshold] = useState(String(MIN_LOW_BALANCE_USD));
  const [lowBalSaving, setLowBalSaving] = useState(false);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaChannel, setTwoFaChannel] = useState<'email' | 'phone'>('email');
  const [emailVerifiedFromApi, setEmailVerifiedFromApi] = useState(false);
  const [phoneVerifiedFromApi, setPhoneVerifiedFromApi] = useState(false);
  const [twoFaSaving, setTwoFaSaving] = useState(false);
  const [costRate, setCostRate] = useState<number | null>(null);
  const [playerAutoApprove, setPlayerAutoApprove] = useState<PlayerWithdrawalAutoApproveSnapshot | null>(null);

  const emailVerified = !!(user?.emailVerified || emailVerifiedFromApi);
  const phoneVerified = !!(user?.phoneVerified || phoneVerifiedFromApi);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [res, thresholdRes] = await Promise.all([
          fetchDashboard(),
          fetchSuperAgentPlayerWithdrawalAutoApprove().catch(() => null),
        ]);
        if (!cancelled && res.success && res.data) {
          setLowBalEnabled(res.data.low_balance_warning_enabled !== false);
          const t = res.data.low_balance_threshold_usd;
          if (t != null && Number.isFinite(Number(t))) {
            setLowBalThreshold(String(Number(t)));
          }
          setTwoFaEnabled(!!res.data.two_factor_enabled);
          setTwoFaChannel(res.data.two_factor_channel === 'phone' ? 'phone' : 'email');
          setEmailVerifiedFromApi(!!res.data.email_verified);
          setPhoneVerifiedFromApi(!!res.data.phone_verified);
          if (res.data.cost_rate != null && Number.isFinite(Number(res.data.cost_rate))) {
            setCostRate(Number(res.data.cost_rate));
          } else if (user?.costRate != null && Number.isFinite(user.costRate)) {
            setCostRate(user.costRate);
          }
        }
        if (!cancelled && thresholdRes?.success && thresholdRes.data) {
          setPlayerAutoApprove(thresholdRes.data);
        }
      } catch {
        if (!cancelled) toast.error('Could not load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppLayout contentFullWidth>
      <h1 className="text-xl font-semibold tracking-tight text-stone-900 mb-6">{PAGE.settings}</h1>

      {loading ? (
        <PageLoadingIndicator variant="page" />
      ) : (
        <>
        <div className="w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <Clock className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-stone-900">Timezone</h2>
              <p className="mt-1 text-xs text-stone-600">
                All timestamps will be displayed in <span className="font-medium">{timezone}</span>.
              </p>
              <div className="mt-3">
                <TimezonePicker className="max-w-xs" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <Percent className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-stone-900">Cost rate</h2>
              <p className="mt-1 text-xs text-stone-600">
                Set by Admin. Used for credit pricing and as the lowest rate you can give your Agents.
              </p>
              <p className="mt-3 text-2xl font-bold tabular-nums tracking-tight text-stone-900">
                {costRate != null ? formatCostRate(costRate) : '—'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <DollarSign className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <PlayerWithdrawalAutoApprovePanel snapshot={playerAutoApprove} viewerLevel="super_agent" />
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-amber-200/90 bg-amber-50/40 p-5 shadow-sm ring-1 ring-amber-100 sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
              <AlertTriangle className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-stone-900">Low balance alerts</h2>
                <p className="mt-1 text-xs text-stone-600">
                  When your credit balance drops below the threshold, you will get a notification and an in-app
                  alert. Minimum threshold is ${MIN_LOW_BALANCE_USD} USD.
                </p>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                  checked={lowBalEnabled}
                  onChange={(e) => setLowBalEnabled(e.target.checked)}
                />
                Enable low balance warnings
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label htmlFor="agent-low-threshold" className="text-xs font-medium text-stone-600">
                    Warning threshold (USD)
                  </label>
                  <input
                    id="agent-low-threshold"
                    type="number"
                    min={MIN_LOW_BALANCE_USD}
                    step="0.01"
                    className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={lowBalThreshold}
                    onChange={(e) => setLowBalThreshold(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  disabled={lowBalSaving}
                  onClick={() => {
                    void (async () => {
                      const n = parseFloat(lowBalThreshold);
                      if (!Number.isFinite(n) || n < MIN_LOW_BALANCE_USD) {
                        toast.error(`Threshold must be at least ${MIN_LOW_BALANCE_USD} USD`);
                        return;
                      }
                      setLowBalSaving(true);
                      try {
                        const res = await patchAgentLowBalanceWarning({
                          low_balance_warning_enabled: lowBalEnabled,
                          low_balance_threshold_usd: n,
                        });
                        if (res.success && res.data) {
                          toast.success('Settings saved');
                          setLowBalEnabled(res.data.low_balance_warning_enabled);
                          setLowBalThreshold(String(res.data.low_balance_threshold_usd));
                        } else {
                          toast.error(res.message || 'Could not save');
                        }
                      } catch {
                        toast.error('Could not save settings');
                      } finally {
                        setLowBalSaving(false);
                      }
                    })();
                  }}
                  className="inline-flex items-center justify-center rounded-lg bg-stone-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-stone-900 disabled:opacity-60"
                >
                  {lowBalSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 w-full rounded-xl border border-stone-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-800">
              <Shield className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <h2 className="text-sm font-semibold text-stone-900">Two-factor authentication</h2>
                <p className="mt-1 text-xs text-stone-600">
                  When enabled, sign-in requires a one-time code sent to your verified email or phone.
                </p>
              </div>
              {!emailVerified && !phoneVerified ? (
                <p className="text-xs text-amber-800">
                  Verify your email or phone before enabling 2FA.
                </p>
              ) : null}
              <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-800">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-stone-300 text-emerald-600 focus:ring-emerald-500"
                  checked={twoFaEnabled}
                  disabled={!emailVerified && !phoneVerified}
                  onChange={(e) => setTwoFaEnabled(e.target.checked)}
                />
                Enable two-factor authentication
              </label>
              {twoFaEnabled && (emailVerified || phoneVerified) ? (
                <fieldset className="space-y-2">
                  <legend className="text-xs font-medium text-stone-600">Send codes via</legend>
                  {emailVerified ? (
                    <label className="flex items-center gap-2 text-sm text-stone-800">
                      <input
                        type="radio"
                        name="sa-two-fa-channel"
                        checked={twoFaChannel === 'email'}
                        onChange={() => setTwoFaChannel('email')}
                      />
                      Email
                    </label>
                  ) : null}
                  {phoneVerified ? (
                    <label className="flex items-center gap-2 text-sm text-stone-800">
                      <input
                        type="radio"
                        name="sa-two-fa-channel"
                        checked={twoFaChannel === 'phone'}
                        onChange={() => setTwoFaChannel('phone')}
                      />
                      SMS (phone)
                    </label>
                  ) : null}
                </fieldset>
              ) : null}
              <button
                type="button"
                disabled={twoFaSaving || (!emailVerified && !phoneVerified)}
                onClick={() => {
                  void (async () => {
                    setTwoFaSaving(true);
                    try {
                      const res = await patchPortalTwoFactor({
                        two_factor_enabled: twoFaEnabled,
                        ...(twoFaEnabled ? { two_factor_channel: twoFaChannel } : {}),
                      });
                      if (res.success && res.data) {
                        toast.success('Two-factor settings saved');
                        setTwoFaEnabled(!!res.data.two_factor_enabled);
                        if (res.data.two_factor_channel === 'phone') setTwoFaChannel('phone');
                        else if (res.data.two_factor_channel === 'email') setTwoFaChannel('email');
                        setEmailVerifiedFromApi(!!res.data.email_verified);
                        setPhoneVerifiedFromApi(!!res.data.phone_verified);
                        if (user && token) {
                          login(
                            {
                              ...user,
                              emailVerified: !!res.data.email_verified,
                              phoneVerified: !!res.data.phone_verified,
                            },
                            token
                          );
                        }
                      } else {
                        toast.error(res.message || 'Could not save');
                      }
                    } catch {
                      toast.error('Could not save two-factor settings');
                    } finally {
                      setTwoFaSaving(false);
                    }
                  })();
                }}
                className="inline-flex items-center justify-center rounded-lg bg-stone-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-stone-900 disabled:opacity-60"
              >
                {twoFaSaving ? 'Saving…' : 'Save 2FA settings'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </AppLayout>
  );
}
